// Third party components
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, Grid, Typography, Card, CardContent, Container, Skeleton } from '@mui/material';

// Shared components

// Own components
import Tabber from '../../components/Tabber';
import TabberPanel from '../../components/TabberPanel';
import ActivityAttachments from './ActivityAttachments';

// helpers
import { formatValue, getDateFormatted, snakeToCamelCase, snakeToPascalCase } from 'helpers';
import { ACTIVITY_SPECIFIC_FIELDS, ACTIVITY_FIELD_LABEL_LOOKUP } from 'constants';

// Tabs are defined here for the sub header
const tabs = [{ index: 0, label: 'Details' }, { index: 1, label: 'Photos / Attachments' }];

// Details config for fields
const staticActivityDetailsConfig = [
  { order: 0, label: 'Activity Name', field: 'activityName' },
  { order: 1, label: 'Requested Date', field: 'requestedDate', isDate: true },
  { order: 2, label: 'Activity Category', field: 'activityCategory' },
  { order: 3, label: 'Planned Start Date', field: 'plannedStartDate', isDate: true },
  { order: 4, label: 'Parent Practice Name', field: 'practiceName' },
  { order: 5, label: 'Started Date', field: 'startedDate', isDate: true },
  { order: 6, label: 'Parent Unit Name', field: 'unitName' },
  { order: 7, label: 'Completed Date', field: 'completedDate', isDate: true },
  { order: 8, label: 'Recipient Name', field: ['individualRecipientName', 'organizationRecipientName'] },
  { order: 9, label: 'Status', field: 'status' },
  { order: 10, label: 'Created By', field: 'createdBy' },
  { order: 11, label: 'Created Date', field: 'createdDate', isDate: true },
]

const getActivityFieldLabel = (field_name) => {
  return ACTIVITY_FIELD_LABEL_LOOKUP[field_name] || snakeToPascalCase(field_name, true);
}

const ActivityDetails = ({ activity, loading }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const activitySpecificFields = ACTIVITY_SPECIFIC_FIELDS[activity?.activityName];

  // Function for handling the tab change
  const handleChange = async (newValue) => {
    setTabIndex(newValue);
  };

  const renderPhotosAndAttachments = () => {
    return (
      <>
        <ActivityAttachments id={activity?.objectid || 0} />
      </>
    );
  }

  const renderActivityDetails = () => {
    if (loading || !activity) {
      return <>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </>
    }

    return (
      <>
        <Typography variant="sectionHeader">Activity Details</Typography>
        <Divider />
        <Grid container spacing={2} sx={{ paddingTop: 3, paddingBottom: 3 }}>
          { /* General Activity Details */}
          {
            staticActivityDetailsConfig.sort((a, b) => a.order - b.order).map((configItem) => {
              return (
                <React.Fragment key={configItem.order}>
                  {/* Label */}
                  <Grid item xs={3} sx={{ paddingTop: 3 }}>
                    <strong>{configItem?.label || 'Label'}: </strong>
                  </Grid>

                  {/* Data */}
                  <Grid item xs={3} alignItems="center" justify="flex-end">
                    {formatValue(activity, configItem)}
                  </Grid>
                </React.Fragment>
              );
            })
          }
        </Grid>
        <Divider />
        <Grid container spacing={2} sx={{ paddingTop: 3, paddingBottom: 3 }}>
          {/* Other Specific Activity Details Depending on Category*/}
          {
            activitySpecificFields
              .map((specificActivityKey, index) => {
                return (
                  <React.Fragment key={`${specificActivityKey}-${index}`}>
                    <Grid item xs={3} sx={{ paddingTop: 3 }}>
                      <strong>{getActivityFieldLabel(specificActivityKey) || 'Label'}: </strong>
                    </Grid>
                    <Grid item xs={3} alignItems="center" justify="flex-end">
                      { (specificActivityKey.endsWith('date')
                        ? getDateFormatted(activity[snakeToCamelCase(specificActivityKey)])
                        : activity[snakeToCamelCase(specificActivityKey)] || 'Not Available')

                      }
                    </Grid>
                  </React.Fragment>
                );
              })
          }
        </Grid>
      </>
    );
  }

  return (
    <>
      <Container maxWidth={false} sx={{ marginTop: 2, marginBottom: 2 }}>
        <Card>
          <CardContent>
            <Typography variant="sectionHeader">Activity</Typography>
          </CardContent>
          <Divider />
          <CardContent>
            <Tabber
              index={tabIndex}
              tabs={tabs}
              onChange={handleChange}
            />
            <Divider />

            {/* 🌏 Main content: Activities List on left, Selected Activity Details on right */}
            <TabberPanel value={tabIndex} index={0}>
              {renderActivityDetails()}
            </TabberPanel>
            <TabberPanel value={tabIndex} index={1}>
              {renderPhotosAndAttachments()}
            </TabberPanel>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

ActivityDetails.propTypes = {
  activity: PropTypes.object,
  loading: PropTypes.bool,
}

export default ActivityDetails;
